import { APP_ENTITIES, APP_MODULES, ENTITY_ACTIONS, } from './enums.js';
/**
 * Every protected capability in the CRM.
 * Naming: `<resource>.<action>`. Keep alphabetical within a resource.
 */
export const PERMISSIONS = [
    'company.read', 'company.write', 'company.delete',
    'contact.read', 'contact.write', 'contact.delete',
    'deal.read', 'deal.write', 'deal.advance', 'deal.delete',
    // delete is admin-only by construction: admin is PERMISSIONS, and the
    // accounting and sales lists below are explicit. Rule 2 — neither deletes.
    'followUp.read', 'followUp.write', 'followUp.delete',
    'audit.read', 'audit.write', 'audit.delete',
    'finding.read', 'finding.write', 'finding.delete',
    // The six deletes added 2026-10-26 (DECISIONS.md §17) are admin only, like
    // every other delete except quotation.delete — none appears in the
    // accounting or sales lists below.
    'certificate.read', 'certificate.write', 'certificate.delete',
    'course.read', 'course.write',
    'session.read', 'session.write', 'session.delete',
    'trainee.read', 'trainee.write', 'trainee.delete',
    'lab.read', 'lab.write', 'lab.delete',
    // quotation.delete is the ONE delete accounting holds — Nemer, 2026-10-26.
    // See DECISIONS.md §15.
    'quotation.read', 'quotation.write', 'quotation.delete',
    'invoice.read', 'invoice.write', 'invoice.delete',
    'payment.write',
    // Return invoices (DECISIONS.md §16). refund.write is separate from
    // creditNote.write for the reason payment.write is separate from
    // invoice.write: reducing a bill and paying money back are different acts.
    // creditNote.delete is admin only, like invoice.delete — it also covers
    // reversing a refund, as invoice.delete covers reversing a payment.
    'creditNote.read', 'creditNote.write', 'creditNote.delete',
    'refund.write',
    'product.read', 'product.write', 'product.delete',
    // Booking a batch straight onto the shelf with no order behind it. Admin
    // only: purchasing is the route stock takes, and this exists for opening
    // stock, a donation, or something bought with petty cash — Nemer, 2026-09-08.
    'product.receiveManual',
    'purchase.read', 'purchase.write', 'purchase.receive', 'purchase.delete',
    'project.read', 'project.write', 'project.delete',
    // HR (DECISIONS.md §13). staff.* covers the person record; leave.approve
    // is separate from leave.write because raising a request and granting
    // one are different acts, and nobody should do both to themselves.
    'staff.read', 'staff.write', 'staff.delete',
    'leave.read', 'leave.write', 'leave.approve',
    'timesheet.read', 'timesheet.write',
    // Accounting — money going out (DECISIONS.md §14). expense.approve is
    // separate from expense.write for the same reason leave.approve is:
    // claiming money back and granting it are different acts, and nobody
    // should do both to their own claim.
    'expense.read', 'expense.write', 'expense.delete', 'expense.approve',
    'purchasePayment.write',
    'report.financial', 'report.operational', 'report.team',
    'import.run',
    'user.read', 'user.manage',
    'settings.manage',
    'auditLog.read',
];
/**
 * The role matrix, per docs/DECISIONS.md §1 and §13. FOUR roles since
 * 2026-09-17 — it was three until the HR role was added.
 *
 * admin      - full control
 * accounting - the money and the reporting: quotations, invoices, payments and
 *              every report. Reads everything else. No user admin, and no
 *              deletes except quotations (DECISIONS.md §15).
 * sales      - the people doing the work: data entry and moving records
 *              forward across the operational modules. No financial reports,
 *              no invoicing, no deletes.
 * hr         - staff records, leave and timesheets. Nothing else at all: no
 *              clients, no deals, no invoices, no reports.
 *
 * This is the single source of truth. The server enforces it on every route;
 * the web app uses it only to hide what the server already forbids.
 *
 * Note what `hr` does NOT have: report.operational, which every other role
 * carries and which the dashboard reads. An HR account therefore has no
 * dashboard to land on, and the sign-in redirect has to send them somewhere
 * they can actually see — handled in the web app, asserted in the tests.
 */
export const ROLE_PERMISSIONS = {
    admin: PERMISSIONS,
    accounting: [
        'company.read',
        'contact.read',
        'deal.read',
        'followUp.read',
        'audit.read',
        'finding.read',
        'certificate.read',
        'course.read',
        'session.read',
        'trainee.read',
        'lab.read',
        // The one exception to "accounting deletes nothing": a quotation is an
        // offer, not a record of money, and accounting owns them (DECISIONS §15).
        'quotation.read', 'quotation.write', 'quotation.delete',
        'invoice.read', 'invoice.write',
        'payment.write',
        // Issue credit notes and pay refunds; never delete either (§16).
        'creditNote.read', 'creditNote.write',
        'refund.write',
        'product.read',
        'purchase.read', 'purchase.write', 'purchase.receive',
        'project.read',
        // No delete: rule 2 gives accounting none anywhere, and an expense is
        // a supporting document — removing one is an administrator act.
        'expense.read', 'expense.write', 'expense.approve',
        'purchasePayment.write',
        'report.financial', 'report.operational', 'report.team',
        'user.read',
        'auditLog.read',
    ],
    sales: [
        'company.read', 'company.write',
        'contact.read', 'contact.write',
        'deal.read', 'deal.write', 'deal.advance',
        'followUp.read', 'followUp.write',
        'audit.read', 'audit.write',
        'finding.read', 'finding.write',
        'certificate.read', 'certificate.write',
        'course.read', 'course.write',
        'session.read', 'session.write',
        'trainee.read', 'trainee.write',
        'lab.read', 'lab.write',
        'quotation.read', 'quotation.write',
        // Raise and edit an invoice, but NOT record a payment against one and NOT
        // delete one. Whoever bills a client stays separate from whoever confirms
        // the money arrived — Nemer's call, 2026-09-08. See DECISIONS.md §1.
        'invoice.read', 'invoice.write',
        // Read only: a salesperson sees what was credited on a client's invoice,
        // and neither issues credit notes nor pays money back (§16).
        'creditNote.read',
        'product.read', 'product.write',
        'purchase.read',
        'project.read', 'project.write',
        // Their OWN expenses only, enforced in the service — a salesperson
        // claims their travel without seeing the company's spending.
        'expense.read', 'expense.write',
        'report.operational',
        'import.run',
    ],
    /**
     * The fourth role, added 2026-09-17 — DECISIONS.md §13.
     *
     * Staff records, leave and timesheets, and nothing else. No companies,
     * no deals, no invoices, no reports: an HR person has no business in the
     * client pipeline, and the whole point of the role was that the screen
     * axis could not express that on its own.
     *
     * user.read is here and user.manage deliberately is NOT. HR needs to see
     * the staff list to attach a record to a person; creating accounts and
     * granting permissions stays with an administrator.
     */
    hr: [
        'staff.read', 'staff.write', 'staff.delete',
        'leave.read', 'leave.write', 'leave.approve',
        'timesheet.read', 'timesheet.write',
        'user.read',
    ],
};
export function can(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
/**
 * Resources where `sales` may only touch rows they own.
 * Enforced server-side in the service layer, never only in the UI.
 */
export const OWNER_SCOPED_FOR_SALES = ['company', 'deal', 'expense'];
// =====================================================================
//  Screen access — the second axis (DECISIONS.md §1)
// =====================================================================
/** Which section each screen is grouped under in the sidebar and the grant UI. */
export const ENTITY_SECTION = {
    dashboard: 'overview', today: 'overview', 'activity-log': 'overview',
    companies: 'pipeline', contacts: 'pipeline', whatsapp: 'pipeline', deals: 'pipeline',
    import: 'pipeline', duplicates: 'pipeline',
    audits: 'compliance', findings: 'compliance', certificates: 'compliance',
    courses: 'academy', sessions: 'academy', trainees: 'academy',
    laboratory: 'operations', inventory: 'operations',
    purchases: 'operations', suppliers: 'operations', projects: 'operations',
    quotations: 'billing', invoices: 'billing', creditNotes: 'billing',
    expenses: 'billing', payables: 'billing',
    staff: 'people', leave: 'people', timesheets: 'people',
    reports: 'administration', team: 'administration',
};
/**
 * What each screen needs in order to work.
 *
 * These are the permissions the screen's OWN forms and pickers call, not just
 * its list — which is why several borrow from a sibling. Getting this wrong in
 * one direction leaves a required dropdown silently empty; in the other it
 * quietly grants a capability the tick-box did not mention.
 */
export const ENTITY_PERMISSIONS = {
    dashboard: ['report.operational'],
    today: ['followUp.read', 'followUp.write', 'followUp.delete'],
    'activity-log': ['followUp.read', 'followUp.delete'],
    // The Log-follow-up button lives on the company record.
    companies: ['company.read', 'company.write', 'followUp.read', 'followUp.write', 'followUp.delete'],
    /**
     * WhatsApp is a VIEW over follow-ups, not a store of its own — click-to-chat
     * means the messages themselves live on people's handsets, and what the CRM
     * holds is the logged record of each exchange (DECISIONS.md §10).
     *
     * So it reads and writes follow-ups, and reads the company and contact it
     * has to name and dial. Broader than the name suggests, and recorded here
     * rather than left to be discovered.
     */
    whatsapp: ['followUp.read', 'followUp.write', 'followUp.delete', 'contact.read', 'company.read'],
    contacts: ['contact.read', 'contact.write', 'contact.delete'],
    deals: ['deal.read', 'deal.write', 'deal.advance', 'deal.delete', 'company.read'],
    // Import writes companies, contacts and deals through its own permission.
    import: ['import.run'],
    // Merging is the only screen that deletes a company.
    duplicates: ['company.read', 'company.delete'],
    // finding.read is deliberate, not an oversight: GET /audits/:id embeds the
    // audit's non-conformities and its score, so granting Audits is a read of
    // finding CONTENT whether or not the Non-conformities screen is granted.
    audits: ['audit.read', 'audit.write', 'audit.delete', 'finding.read'],
    // The New Finding dialog's Audit picker is required and reads audit.read.
    findings: ['finding.read', 'finding.write', 'finding.delete', 'audit.read'],
    // "Renew now" opens a deal.
    certificates: ['certificate.read', 'certificate.write', 'certificate.delete', 'deal.write'],
    courses: ['course.read', 'course.write'],
    // Enrolments are gated on trainee.*, not session.*, and the form picks a course.
    // Deliberately NOT trainee.delete: removing a person from the register is
    // the Trainees screen's act, and granting Sessions must not carry it.
    sessions: [
        'session.read', 'session.write', 'session.delete', 'course.read', 'trainee.read', 'trainee.write',
    ],
    trainees: ['trainee.read', 'trainee.write', 'trainee.delete'],
    laboratory: ['lab.read', 'lab.write', 'lab.delete'],
    // The supplier picker on a product reads the suppliers list. receiveManual
    // is here rather than on Purchases: it is the escape hatch on the stock
    // screen, not part of buying.
    inventory: ['product.read', 'product.write', 'product.delete', 'product.receiveManual'],
    /**
     * Broader than its name: the order form picks a product and a supplier, and
     * both of those lists are gated on product.read. Recorded here rather than
     * left to be discovered, like Audits and Quotations before it.
     */
    purchases: ['purchase.read', 'purchase.write', 'purchase.receive', 'purchase.delete', 'product.read'],
    suppliers: ['product.read', 'product.write'],
    projects: ['project.read', 'project.write', 'project.delete'],
    // Converting a quotation raises the invoice — Nemer's call, 2026-09-04.
    quotations: ['quotation.read', 'quotation.write', 'quotation.delete', 'invoice.write'],
    invoices: ['invoice.read', 'invoice.write', 'invoice.delete', 'payment.write'],
    /**
     * Return invoices (§16). Borrows invoice.read because every credit note is
     * raised from an invoice's lines and links back to it. Deliberately NOT
     * invoice.write or payment.write: granting Credit notes must not quietly
     * grant raising invoices or recording payments.
     */
    creditNotes: [
        'creditNote.read', 'creditNote.write', 'creditNote.delete', 'refund.write', 'invoice.read',
    ],
    /**
     * Money going out (§14). The form picks a supplier, a client, a project
     * and a deal, so it borrows all four reads — recorded here rather than
     * left to be discovered when a dropdown turns up empty.
     */
    expenses: [
        'expense.read', 'expense.write', 'expense.delete', 'expense.approve',
        'company.read', 'project.read', 'deal.read', 'product.read',
    ],
    // What MEFOSA owes suppliers, and settling it against an order.
    // report.financial is what actually gates it: sales holds purchase.read
    // and has no business reading the company's supplier debt.
    payables: ['purchase.read', 'purchasePayment.write', 'report.financial'],
    /**
     * The three HR screens. Each borrows user.read: all of them name a
     * colleague, and a picker with no names is a broken screen.
     */
    staff: ['staff.read', 'staff.write', 'staff.delete', 'user.read'],
    leave: ['leave.read', 'leave.write', 'leave.approve', 'staff.read', 'user.read'],
    timesheets: ['timesheet.read', 'timesheet.write', 'staff.read', 'user.read'],
    // One entity, not two: the screen cannot honestly separate its tabs yet.
    reports: ['report.team', 'report.financial', 'report.operational'],
    team: ['user.read', 'user.manage', 'settings.manage', 'auditLog.read'],
};
const everyPermissionReachable = true;
void everyPermissionReachable;
/**
 * Screens nobody can be denied.
 *
 * The dashboard is where signing in lands, and where the catch-all route
 * redirects; denying it would refuse the page it then bounces to.
 */
export const ALWAYS_GRANTED_ENTITIES = ['dashboard'];
/**
 * The screens this person actually has.
 *
 * An admin always has all of them: an administrator who could tick themselves
 * out of Team would leave nobody able to undo it.
 */
export function effectiveEntities(who) {
    if (who.role === 'admin' || who.entities === null)
        return APP_ENTITIES;
    const granted = new Set([...who.entities, ...ALWAYS_GRANTED_ENTITIES]);
    return APP_ENTITIES.filter((e) => granted.has(e));
}
export function hasEntity(who, entity) {
    return effectiveEntities(who).includes(entity);
}
/** A section is shown when any screen inside it is granted. */
export function hasModule(who, module) {
    return effectiveEntities(who).some((e) => ENTITY_SECTION[e] === module);
}
/** Every screen in a section, for the "tick the whole section" control. */
export function entitiesInModule(module) {
    return APP_ENTITIES.filter((e) => ENTITY_SECTION[e] === module);
}
/**
 * The effective check: the ROLE says what you may do, the granted screens say
 * where. Both must allow it.
 *
 * Note what this deliberately does NOT do: it cannot tell apart two screens
 * that need the same permission. Granting Sessions allows reading trainees,
 * because the enrolment dialog needs it. Unticking Trainees hides that SCREEN;
 * it does not put the data out of reach of a screen that was granted. The
 * grant UI says so rather than implying otherwise.
 */
export function canAccess(who, permission) {
    if (!can(who.role, permission))
        return false;
    return effectiveEntities(who).some((e) => ENTITY_PERMISSIONS[e].includes(permission));
}
/** The screens a permission belongs to, for an error that names the real cause. */
export function entitiesForPermission(permission) {
    return APP_ENTITIES.filter((e) => ENTITY_PERMISSIONS[e].includes(permission));
}
/**
 * Whether this person may take this action on this screen.
 *
 * NARROWING ONLY, and that is the whole design. It can take an action away
 * from somebody whose role allows it; it can never give one to somebody whose
 * role does not — Nemer's call, 2026-09-08. If a grant could exceed the role,
 * knowing what anybody can do would mean opening their profile rather than
 * reading the three-role matrix, and one mis-ticked box would hand a
 * salesperson delete on invoices.
 *
 * An administrator is never narrowed: an admin who could tick themselves out
 * of editing users would leave nobody able to undo it.
 */
export function canDoAction(who, entity, action) {
    if (who.role === 'admin')
        return true;
    // The screen must be reachable at all before any action on it can be.
    if (!hasEntity(who, entity))
        return false;
    const granted = who.entityActions?.[entity];
    // Absent means "not narrowed", which is different from an empty list meaning
    // "narrowed to nothing". Conflating them would silently lock out every
    // account that predates this feature.
    if (granted === undefined || granted === null)
        return true;
    return granted.includes(action);
}
/**
 * The actions a person actually has on a screen, for the UI to draw with.
 *
 * Reports what is TRUE, not what was ticked: an administrator comes back with
 * all three whatever their record says.
 */
export function actionsFor(who, entity) {
    return ENTITY_ACTIONS.filter((a) => canDoAction(who, entity, a));
}
/**
 * Whether this ROLE could ever take this action on this screen.
 *
 * Used to decide which ticks the grant UI should draw at all. Sales holds no
 * `*.delete` permission anywhere, so a delete tick on their profile could
 * never change an outcome in either direction — it looked like a control and
 * was inert, which is the same failure as a hidden button pretending to be a
 * permission, only inverted.
 *
 * Answers from the permission matrix rather than a hand-written list, so a
 * role gaining a delete makes the tick appear on its own.
 */
export function roleCanAction(role, entity, action) {
    // An administrator is never narrowed, so every tick is meaningless for them.
    if (role === 'admin')
        return false;
    const needed = action === 'delete' ? '.delete' : '.write';
    return ENTITY_PERMISSIONS[entity]
        .filter((p) => p.endsWith(needed))
        .some((p) => can(role, p));
}
//# sourceMappingURL=permissions.js.map